import { messaging, getToken, onMessage } from '@/firebase-config';
import axios from '@/axios';
import store from '@/store';

export class NotificationManager {
  constructor() {
    this.isSupported = 'Notification' in window && 'serviceWorker' in navigator;
    this.currentToken = null;
  }

  async init() {
    if (!this.isSupported) {
      console.warn('Las notificaciones no son soportadas en este navegador');
      return false;
    }

    try {
      // Registrar el service worker
      if ('serviceWorker' in navigator) {
        await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        console.log('Service Worker registrado exitosamente');
      }

      // Configurar listener para mensajes en primer plano
      this.setupForegroundMessageListener();
      
      return true;
    } catch (error) {
      console.error('Error inicializando el gestor de notificaciones:', error);
      return false;
    }
  }

  async requestPermission() {
    if (!this.isSupported) {
      throw new Error('Las notificaciones no son soportadas');
    }

    const permission = await Notification.requestPermission();
    
    if (permission !== 'granted') {
      throw new Error('Permisos de notificación denegados');
    }

    return permission;
  }

  async getToken() {
    try {
      if (!messaging) {
        throw new Error('Firebase Messaging no está inicializado');
      }

      const vapidKey = process.env.VUE_APP_FIREBASE_VAPID_KEY;
      if (!vapidKey) {
        throw new Error('VAPID key no configurada');
      }

      const token = await getToken(messaging, { vapidKey });
      this.currentToken = token;
      return token;
    } catch (error) {
      console.error('Error obteniendo token FCM:', error);
      throw error;
    }
  }

  async sendTokenToServer(token) {
    try {
      if (!store.getters['auth/isAuthenticated']) {
        console.warn('Usuario no autenticado, no se puede enviar el token');
        return false;
      }

      const response = await axios.post(
        `${process.env.VUE_APP_API_BASE_URL}/api/accounts/fcm-token/`,
        { fcm_token: token },
        { withCredentials: true }
      );

      console.log('Token FCM enviado al servidor exitosamente');
      return response.data;
    } catch (error) {
      console.error('Error enviando token al servidor:', error);
      throw error;
    }
  }

  async enableNotifications() {
    try {
      await this.requestPermission();
      const token = await this.getToken();
      await this.sendTokenToServer(token);
      
      // Guardar estado en localStorage
      localStorage.setItem('fcm_notifications_enabled', 'true');
      localStorage.setItem('fcm_token', token);
      
      return token;
    } catch (error) {
      console.error('Error habilitando notificaciones:', error);
      throw error;
    }
  }

  setupForegroundMessageListener() {
    if (!messaging) return;

    onMessage(messaging, (payload) => {
      console.log('Mensaje recibido en primer plano:', payload);
      
      // Mostrar notificación personalizada cuando la app está en primer plano
      this.showForegroundNotification(payload);
    });
  }

  showForegroundNotification(payload) {
    const title = payload.notification?.title || 'Nueva notificación';
    const body = payload.notification?.body || 'Tienes una nueva actualización';
    
    // Crear una notificación del navegador
    if (Notification.permission === 'granted') {
      const notification = new Notification(title, {
        body,
        icon: '/img/icons/android-chrome-192x192.png',
        tag: 'inmob-foreground',
        data: payload.data || {}
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
        
        // Aquí puedes agregar lógica para navegar a una página específica
        if (payload.data?.url) {
          window.location.href = payload.data.url;
        }
      };

      // Auto-cerrar después de 5 segundos
      setTimeout(() => {
        notification.close();
      }, 5000);
    }
  }

  isEnabled() {
    return localStorage.getItem('fcm_notifications_enabled') === 'true';
  }

  async disable() {
    try {
      // Remover token del servidor si el usuario está autenticado
      if (store.getters['auth/isAuthenticated'] && this.currentToken) {
        await axios.delete(
          `${process.env.VUE_APP_API_BASE_URL}/api/accounts/fcm-token/`,
          { withCredentials: true }
        );
      }
      
      // Limpiar localStorage
      localStorage.removeItem('fcm_notifications_enabled');
      localStorage.removeItem('fcm_token');
      this.currentToken = null;
      
      console.log('Notificaciones deshabilitadas');
    } catch (error) {
      console.error('Error deshabilitando notificaciones:', error);
      throw error;
    }
  }
}

// Instancia global del gestor de notificaciones
export const notificationManager = new NotificationManager();

// Funciones de conveniencia para compatibilidad
export async function requestNotificationPermission() {
  return await notificationManager.enableNotifications();
}

export function isNotificationSupported() {
  return notificationManager.isSupported;
}

export function areNotificationsEnabled() {
  return notificationManager.isEnabled();
}